import qrcode from "qrcode-generator";
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";

export function absolute(link: string): string {
  try {
    return new URL(link, location.origin).href;
  } catch {
    return link;
  }
}

export function Qr({ url, big }: { url: string; big?: boolean }) {
  const svg = useMemo(() => {
    const qr = qrcode(0, "M");
    qr.addData(url);
    qr.make();
    return qr.createSvgTag({ cellSize: 3, margin: 2 });
  }, [url]);
  return <div className={"ad-qr" + (big ? " big" : "")} dangerouslySetInnerHTML={{ __html: svg }} />;
}

/**
 * A link as one short row: its name, Copy, and a QR code that opens under it on a tap. `startOpen`
 * shows the code straight away (a link just made, to scan there and then) and brings it to the
 * middle of the screen; `note` is a small line under the name; `extra` is a line of keys under
 * the row.
 */
export function LinkRow({ label, link, say, startOpen = false, note, extra }: {
  label: string;
  link: string;
  say: (s: string) => void;
  startOpen?: boolean;
  note?: string;
  extra?: ReactNode;
}) {
  const [open, setOpen] = useState(startOpen);
  const row = useRef<HTMLLIElement | null>(null);
  // a code opened for someone to scan now is no use under the fold: the whole of it on the glass
  useEffect(() => {
    if (startOpen) row.current?.querySelector(".ad-link-open")?.scrollIntoView({ block: "center", behavior: "smooth" });
  }, [startOpen]);
  const url = absolute(link);
  async function copy() {
    try {
      await navigator.clipboard.writeText(url);
      say(`${label} link copied.`);
    } catch {
      say("Could not copy. Open the QR and press and hold the link instead.");
    }
  }
  return (
    <li ref={row} className={"ad-link" + (open ? " open" : "")}>
      <div className="ad-link-row">
        <span className="ad-link-label">
          {label}
          {note && <span className="ad-link-note">{note}</span>}
        </span>
        <button type="button" className="ad-small" onClick={copy}>Copy</button>
        <button type="button" className={"ad-small" + (open ? " on" : "")} aria-expanded={open} onClick={() => setOpen(!open)}>QR</button>
      </div>
      {open && (
        <div className="ad-link-open">
          <Qr url={url} big />
          <div className="ad-link-url">{url}</div>
        </div>
      )}
      {extra && <div className="ad-link-extra">{extra}</div>}
    </li>
  );
}
